import { getNodeIndexMap } from "../actionTree";
import { poiMissionFilter, simulateMissionExecution } from "../simLite";

import type { StateCreator } from "zustand";
import type { GPSPoint } from "@skydio/math";
import type { Mission } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission";
import type { NodeIndexMap } from "../actionTree";
import type { SimulationResult } from "../simLite";

// ============================================================================
// Utility functions
// These are copied from customer portal so that when customer portal switches
// to using this library, there's minimal diff.
// - getMissionPreviewWorldOrigin: from _lib/missions/utils/getMissionPreviewWorldOrigin
//   NOTE: The portal version fetches world origin from GraphQL (mission template takeoff point,
//   terrain elevation). This shared version provides a simplified synchronous fallback.
//   Portal will override or extend this with its async GraphQL-based implementation.
// ============================================================================

/**
 * Simplified world origin extraction for mission preview.
 * The portal version (getMissionPreviewWorldOrigin) does async GraphQL fetching
 * for template takeoff points and terrain elevation. This shared version
 * only uses data available in the mission spec itself.
 */
function getMissionPreviewWorldOrigin(missionSpec: Mission): GPSPoint | undefined {
  // Try expectedGpsOrigin from mission spec
  if (missionSpec.expectedGpsOrigin) {
    const { lat, lon, gpsAltitude } = missionSpec.expectedGpsOrigin;
    if (lat != null && lon != null) {
      return { lat, lng: lon, alt: gpsAltitude ?? 0 };
    }
  }

  // Try first waypoint GPS coordinates
  const rootSequenceActions = missionSpec.actions?.[0]?.args?.sequence?.actions;
  if (rootSequenceActions) {
    for (const waypointSeq of rootSequenceActions) {
      const gotoAction = waypointSeq.args?.sequence?.actions?.find(
        a => a.actionKey.toLowerCase() === "gotowaypoint"
      );
      const waypoint = gotoAction?.args?.gotoWaypoint?.waypoint;
      if (waypoint?.xy?.x != null && waypoint?.xy?.y != null && waypoint?.xy?.frame === 3) {
        // frame 3 = GPS
        return {
          lat: waypoint.xy.x,
          lng: waypoint.xy.y,
          alt: waypoint.z?.value ?? 0,
        };
      }
    }
  }

  return undefined;
}

// ============================================================================
// Types
// ============================================================================

/**
 * Represents a mission update operation
 */
export type MissionPreview = {
  /** The mission spec to preview */
  spec: Mission;
  /** Node index map for efficient traversal */
  _nodeIndexMap: Record<string, readonly number[]>;
  /** Simulation result for the mission previewed */
  simulationResult?: SimulationResult;
};

export interface MissionsSlice {
  missionPreview: MissionPreview | null;
  setMissionPreview: (mission: Mission | null) => void;
  missions: Record<
    string,
    {
      spec: Mission;
      _nodeIndexMap: NodeIndexMap;
      simulationResult?: SimulationResult;
    }
  >;
  upsertMission: ({
    mission,
    nodeIndexMap,
    simulationResult,
  }: {
    mission: Mission;
    nodeIndexMap?: NodeIndexMap;
    simulationResult?: SimulationResult;
  }) => void;
  removeMission: (key: string) => void;
  resetMissions: () => void;

  // Current-mission editor support
  currentMissionId: string | null;
  loadMission: (mission: Mission, nodeIndexMap?: NodeIndexMap) => void;
  setCurrentMission: (mission: Mission) => void;
  updateCurrentMission: (updates: { spec?: Mission; _nodeIndexMap?: NodeIndexMap }) => void;
}

export const createMissionsSlice: StateCreator<MissionsSlice, [], [], MissionsSlice> = set => {
  return {
    missionPreview: null,
    setMissionPreview: async missionPreview => {
      if (!missionPreview) {
        set(() => ({ missionPreview: null }));
        return;
      }
      const missionWorldOrigin = getMissionPreviewWorldOrigin(missionPreview);
      const isDynamicMission = poiMissionFilter(missionPreview);
      // For now only simulate non-POI
      // NOTE (Ignacio): Is there a reason we cannot do POI? Maybe we can
      // NOTE (Ignacio): Should we wait or re-set sim result afterwards like in other places?
      // Issue is that in this case we cna fall in race conditions if the sim is slow, so I am
      // waiting for the result for now making it synchronous.
      const simulationResult =
        isDynamicMission || !missionWorldOrigin
          ? undefined
          : await simulateMissionExecution(missionPreview, {
              worldOrigin: missionWorldOrigin,
            });
      set(() => ({
        missionPreview:
          missionPreview !== null
            ? {
                spec: missionPreview,
                _nodeIndexMap: getNodeIndexMap(missionPreview),
                simulationResult: simulationResult,
              }
            : null,
      }));
    },
    missions: {},
    upsertMission: ({ mission, nodeIndexMap, simulationResult }) => {
      set(state => ({
        missions: {
          ...state.missions,
          [mission.uuid]: {
            spec: mission,
            _nodeIndexMap: nodeIndexMap ?? getNodeIndexMap(mission),
            simulationResult,
          },
        },
      }));
    },
    removeMission: key =>
      set(state => {
        const newMissions = { ...state.missions };
        delete newMissions[key];
        return {
          missions: newMissions,
          currentMissionId: state.currentMissionId === key ? null : state.currentMissionId,
        };
      }),
    resetMissions: () => set({ missions: {}, currentMissionId: null }),

    // Current-mission editor support
    currentMissionId: null,
    loadMission: (mission, nodeIndexMap?) => {
      set(state => ({
        missions: {
          ...state.missions,
          [mission.uuid]: {
            spec: mission,
            _nodeIndexMap: nodeIndexMap ?? getNodeIndexMap(mission),
          },
        },
        currentMissionId: mission.uuid,
      }));
    },
    setCurrentMission: mission => {
      set(state => ({
        missions: {
          ...state.missions,
          [mission.uuid]: {
            spec: mission,
            _nodeIndexMap: getNodeIndexMap(mission),
          },
        },
        currentMissionId: mission.uuid,
      }));
    },
    updateCurrentMission: updates => {
      set(state => {
        const id = state.currentMissionId;
        if (id == null || !(id in state.missions)) return state;
        const current = state.missions[id];
        return {
          missions: {
            ...state.missions,
            [id]: {
              ...current,
              ...(updates.spec !== undefined ? { spec: updates.spec } : {}),
              ...(updates._nodeIndexMap !== undefined
                ? { _nodeIndexMap: updates._nodeIndexMap }
                : {}),
            },
          },
        };
      });
    },
  };
};
